import type { EmailBlock, EmailGlobalStyles } from '../HtmlEditor.types';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function blockWrapperStyle(block: EmailBlock): string {
  const styles = [
    `padding:${block.paddingTop}px ${block.paddingRight}px ${block.paddingBottom}px ${block.paddingLeft}px`,
    `margin:${block.marginTop}px ${block.marginRight}px ${block.marginBottom}px ${block.marginLeft}px`,
    `text-align:${block.align}`,
  ];
  if (block.bg && block.bg !== 'transparent') {
    styles.push(`background-color:${block.bg}`);
  }
  if (block.bgImage) {
    styles.push(`background-image:url(${block.bgImage})`, 'background-size:cover');
  }
  return styles.join(';');
}

function renderBlock(block: EmailBlock): string {
  const wrap = (inner: string) =>
    `<tr><td style="${blockWrapperStyle(block)};width:${block.width}%">${inner}</td></tr>`;

  switch (block.type) {
    case 'title':
      return wrap(
        `<h2 style="margin:0;font-size:${block.size}px;color:${block.color};font-family:${block.font},Arial,sans-serif;font-weight:${block.weight};line-height:${block.lineHeight}">${escapeHtml(block.text)}</h2>`
      );
    case 'text':
      return wrap(
        `<p style="margin:0;font-size:${block.size}px;color:${block.color};font-family:${block.font},Arial,sans-serif;font-weight:${block.weight};line-height:${block.lineHeight};white-space:pre-wrap">${escapeHtml(block.text)}</p>`
      );
    case 'image':
      if (block.src) {
        return wrap(`<img src="${escapeHtml(block.src)}" alt="" style="max-width:100%;height:${block.height}px;display:block;margin:0 auto" />`);
      }
      return wrap(
        `<div style="height:${block.height}px;background:#e2e8f0;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:14px">Image placeholder</div>`
      );
    case 'video':
      return wrap(
        `<div style="height:${block.height}px;background:#0f172a;color:#fff;display:flex;align-items:center;justify-content:center;border-radius:4px">▶ ${escapeHtml(block.label)}</div>`
      );
    case 'button':
      return wrap(
        `<a href="${escapeHtml(block.href)}" style="display:inline-block;background:${block.btnBg};color:${block.btnColor};padding:${block.paddingTop}px 24px ${block.paddingBottom}px;border-radius:${block.radius}px;font-weight:700;text-decoration:none;font-size:${block.size}px">${escapeHtml(block.text)}</a>`
      );
    case 'dynamic':
      return wrap(
        `<span style="background:#e8ecfb;color:#1530c7;padding:4px 8px;border-radius:4px;font-family:monospace;font-size:12px">{{ ${escapeHtml(block.source)} | ${escapeHtml(block.fallback)} }}</span>`
      );
    case 'logo':
      return wrap(
        `<div style="font-family:${block.font},Arial,sans-serif;font-weight:${block.weight};font-size:${block.size}px;color:${block.color}">${escapeHtml(block.text)}</div>`
      );
    case 'social': {
      const links = block.networks
        .map((n) => `<a href="#" style="display:inline-block;width:28px;height:28px;line-height:28px;text-align:center;border-radius:50%;background:#0f172a;color:#fff;text-decoration:none;font-size:12px;font-weight:700;margin:0 4px">${escapeHtml(n.charAt(0).toUpperCase())}</a>`)
        .join('');
      return wrap(`<div style="text-align:center">${links}</div>`);
    }
    case 'html':
      return wrap(block.code);
    case 'divider':
      return wrap(`<hr style="border:0;height:${block.thickness}px;background:${block.color};margin:0" />`);
    case 'product':
      return wrap(
        `<div style="text-align:center"><div style="font-size:48px">${escapeHtml(block.img)}</div><div style="font-weight:700;margin-top:6px">${escapeHtml(block.name)}</div><div style="color:#1530c7;font-weight:700">${escapeHtml(block.price)}</div></div>`
      );
    case 'nav': {
      const items = block.items
        .map((it) => `<a href="#" style="color:${block.color};text-decoration:none;font-weight:600;font-size:13px;margin:0 8px">${escapeHtml(it)}</a>`)
        .join('');
      return wrap(`<div style="text-align:center">${items}</div>`);
    }
    case 'spacer':
      return wrap(`<div style="height:${block.height}px;line-height:${block.height}px;font-size:1px">&nbsp;</div>`);
    default:
      return '';
  }
}

export function serializeBlocksToHtml(blocks: EmailBlock[], globalStyles?: Partial<EmailGlobalStyles>): string {
  const width = globalStyles?.emailWidth ?? 600;
  const bg = globalStyles?.backgroundColor ?? '#F4F5F7';
  const contentBg = globalStyles?.contentBackground ?? '#FFFFFF';
  const rows = blocks.map(renderBlock).join('\n');

  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:${bg};font-family:Arial,sans-serif">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${bg}">
<tr><td align="center" style="padding:24px 0">
<table role="presentation" width="${width}" cellpadding="0" cellspacing="0" style="max-width:${width}px;background-color:${contentBg}">
${rows}
</table>
</td></tr>
</table>
</body>
</html>`;
}
